/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState } from 'react';

import { css, jsx } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import { token } from '@atlaskit/tokens';

import { Initials } from './initials';

const initialsAvatarCSS = xcss({
	alignItems: 'center',
	display: 'flex',
});

const profilePicAvatarCSS = xcss({
	alignItems: 'center',
	display: 'flex',
	borderRadius: 'radius.full',
	overflow: 'hidden',
});

const profilePicXcss = css({
	width: '100%',
	height: '100%',
});

/** Adapted from @atlaskit/avatar */
const AVATAR_SIZES = {
	xxsmall: xcss({
		width: token('space.200'),
		height: token('space.200'),
	}),
	xsmall: xcss({
		width: token('space.200'),
		height: token('space.200'),
	}),
	// custom smaller size of 20px
	smaller: xcss({
		width: token('space.250'),
		height: token('space.250'),
	}),
	small: xcss({
		width: token('space.300'),
		height: token('space.300'),
	}),
	medium: xcss({
		width: token('space.400'),
		height: token('space.400'),
	}),
	large: xcss({
		width: token('space.500'),
		height: token('space.500'),
	}),
	xlarge: xcss({
		width: `calc(${token('space.600')} * 2)`,
		height: `calc(${token('space.600')} * 2)`,
	}),
	xxlarge: xcss({
		width: `calc(${token('space.800')} * 2)`,
		height: `calc(${token('space.800')} * 2)`,
	}),
};

export type AvatarSize =
	| 'xxsmall'
	| 'xsmall'
	| 'smaller'
	| 'small'
	| 'medium'
	| 'large'
	| 'xlarge'
	| 'xxlarge';

export type AvatarInitials = {
	type: 'initials';
	name: string;
};

export type AvatarProfilePic = {
	type: 'profile-pic';
	src: string;
	name: string; // fallback for image loading error
};

export type AvatarProps = {
	/**
	 * The variant of the avatar to render
	 * @example { variant: 'initials', name: 'Peter Parker' }
	 * @example { variant: 'profile-pic', profilePictureUrl: 'https://example.com/peter-parker.jpg' }
	 */
	variant: AvatarInitials | AvatarProfilePic;
	/**
	 * The size of the avatar
	 */
	size?: AvatarSize;
};

export const Avatar: ({ variant, size }: AvatarProps) => JSX.Element = ({
	variant,
	size = 'medium',
}: AvatarProps) => {
	const [hasImageErrored, setHasImageErrored] = useState(false);

	if (variant.type === 'profile-pic' && !hasImageErrored) {
		const { src } = variant;
		return (
			<Box
				aria-hidden={true}
				testId="search-ui-avatar"
				xcss={[profilePicAvatarCSS, AVATAR_SIZES[size]]}
			>
				<img
					css={profilePicXcss}
					src={src}
					onError={() => setHasImageErrored(true)}
					alt={`${variant.name} avatar`}
				/>
			</Box>
		);
	}

	const { name } = variant;
	return (
		<Box
			aria-hidden={true}
			testId="search-ui-avatar"
			xcss={[initialsAvatarCSS, AVATAR_SIZES[size]]}
		>
			<Initials name={name} />
		</Box>
	);
};
